import { CacheHelper } from "../../../utilities/CacheHelper";
import { logger } from "../../../utilities/logger";
import { DocubirdCache } from "../config/constants";
import { ISelectedMailboxTarget } from "./selectedExchangeItemsStore";

// Office.js only reveals a shared mailbox's identity through a SINGLE open
// item's shared properties, and OWA additionally hides that API on items
// loaded via loadItemByIdAsync. So the identity is harvested wherever the
// platform offers it (any single item the user ever opens) and PROVEN against
// a concrete selection before use: Exchange message ids resolve only inside
// the mailbox that owns them, so one $select=id GET settles ownership.
//
// Contexts are validation CANDIDATES, not truth — a small persisted
// most-recent-first list is safe because nothing is used unproven. Persisted
// (account-scoped localStorage) because the multi-select can happen in a
// later session than the click that revealed the mailbox.

const maxSharedMailboxContexts: number = 5;

export const getKnownSharedMailboxContexts = (): ISelectedMailboxTarget[] => {
    const contexts: ISelectedMailboxTarget[] = CacheHelper.get(DocubirdCache.sharedMailboxContexts, true) ?? [];
    return contexts.filter(context => context?.targetMailbox);
};

export const recordSharedMailboxContext = (context: ISelectedMailboxTarget): void => {
    try {
        if (!context?.targetMailbox) {
            return;
        }
        const updatedContexts: ISelectedMailboxTarget[] = [
            context,
            ...getKnownSharedMailboxContexts().filter(known => known.targetMailbox !== context.targetMailbox)
        ].slice(0, maxSharedMailboxContexts);
        CacheHelper.set(DocubirdCache.sharedMailboxContexts, updatedContexts, true);
    } catch (error) {
        logger(error, "sharedMailboxDiscovery.ts (recordSharedMailboxContext)");
    }
};

/**
 * Resolves which mailbox owns the message with the given rest id, or null
 * when it is the user's own mail (or ownership cannot be proven). /me is
 * checked first: a hit means every recorded context is irrelevant here.
 */
export const discoverMailboxTarget = async (client: any, firstMessageRestId: string): Promise<ISelectedMailboxTarget | null> => {
    const candidates: ISelectedMailboxTarget[] = getKnownSharedMailboxContexts();
    if (!candidates.length || !firstMessageRestId) {
        return null;
    }

    try {
        await client.api(`/me/messages/${firstMessageRestId}`).select("id").get();
        // Proven own-mailbox: the candidates do not apply to this selection.
        return null;
    } catch {
        // Not in /me — a shared mailbox owns this selection; find which one.
    }

    for (const candidate of candidates) {
        try {
            await client.api(`/users/${candidate.targetMailbox}/messages/${firstMessageRestId}`).select("id").get();
            return candidate;
        } catch (error) {
            logger(error, "sharedMailboxDiscovery.ts (discoverMailboxTarget)");
        }
    }

    return null;
};
